import React, { useEffect, useState } from "react";
import Form from "react-bootstrap/Form";
import "./selfonboarding.scss";
import Button from "../../base/Button/Button";
import { Typography } from "src/latest/src/components";
import { Link } from "react-router-dom";
import { isEmpty } from "lodash";
import Input from "src/containers/QuestionnaireParent/QuestionOptionsLibrary/components/Input/Input";
import {
    formatPhoneNumber,
    isValidEmail,
    isValidPhoneNumber,
} from "src/utils/helpers";
import { Choice } from "./type";
import EmailAutocompleteDomainSuggestion from "src/latest/src/components/EmailAutocompleteDomainSuggestion/EmailAutocompleteDomainSuggestion";

type Props = {
    choices: Array<Choice>,
    onSubmit: Function,
    loading: boolean,
};

const SelfOnboarding = ({ choices, onSubmit, loading }: Props) => {
    const [formData, setFormData] = React.useState({});
    const [touched, setTouched] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [validated, setValidated] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);

    function getKeyName(key: string): string {
        let name = key;

        if (key === "email_address") {
            name = "email";
        }

        if (key === "phone_number") {
            name = "phone_no";
        }

        return name;
    }

    useEffect(() => {
        const _formData: any = {};

        choices?.forEach((choice: Choice) => {
            const key = choice?.place_holder?.toLowerCase()?.replace(/\s+/g, "_");
            const name = getKeyName(key);

            _formData[name] = undefined;
        });
        setFormData(_formData);
    }, [choices]);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const validationCharLength = e?.target?.data_validation_char_length || e?.target?.getAttribute("data_validation_char_length");

        handleOnChange(name, value, validationCharLength);
    };

    const handleOnChange = (name: string, value: string, validationCharLength: string) => {
        if (name === "phone_no") {
            value = formatPhoneNumber(value);
        }

        const _errorState = {
            ...errors,
            [name]: inValidMessage(name, value, validationCharLength),
        };
        const _formData = { ...formData, [name]: value };
        const isErrorExist: boolean = checkErrorExist(_errorState);
        const isAllDataEntered: boolean = checkAllDataEntered(_formData);

        setValidated(!isErrorExist);
        setTouched({ ...touched, [name]: true });
        setFormData(_formData);
        setErrors(_errorState);
        setIsDisabled(isErrorExist || !isAllDataEntered);
    };

    const inValidMessage = (
        type: string,
        value: string,
        validationCharLength: string | null
    ) => {
        const trimmedValue = value.trim();
        
        switch (type) {
            case "email":
                return isEmpty(trimmedValue)
                    ? "Please enter email address"
                    : !isValidEmail(trimmedValue)
                    ? "Invalid email"
                    : null;
            case "first_name":
                return isEmpty(trimmedValue) ? "Please enter first name" : null;
            case "last_name":
                return isEmpty(trimmedValue) ? "Please enter last name" : null;
            case "phone_no":
                return isEmpty(trimmedValue)
                    ? "Please enter phone number"
                    : !isValidPhoneNumber(
                          trimmedValue,
                          validationCharLength
                              ? parseInt(validationCharLength)
                              : 10
                      ) // TO use validation_char_length
                    ? "Please enter a 10 digit phone number"
                    : null;
            default:
                return null;
        }
    };

    const checkErrorExist = (errors: any): boolean => {
        return !Object.values(errors)?.every(
            (e) => e === null || e === undefined
        );
    };

    const checkAllDataEntered = (formData: any): boolean => {
        let hasEmptyValue = false;

        for (const i in formData) {
            if (!formData[i] || formData[i].trim() === "") {
                hasEmptyValue = true;
                break;
            }
        }

        return !hasEmptyValue;
    };

    const handleSubmit = (event: React.FormEvent<HTMLInputElement>) => {
        event.preventDefault();
        setIsDisabled(true);
        setValidated(true);
        onSubmit(formData);
    };

    return (
      <div className="self-onboarding">
        <div className="d-flex justify-content-center align-items-center w-100 h-100">
          <div className="form-container">
            <Typography
              variant="size-34"
              color="color-2"
              weight="regular"
              className="text-center self-onboarding-title"
            >
              Verify your email to continue booking
            </Typography>
            <div>
              <Form
                noValidate={true}
                onSubmit={handleSubmit}
                className={`form-self-onboarding mx-auto align-items-center `}
              >
                {
                  <div className="text-center self-onboarding-text">
                    <Typography
                      variant="size-16"
                      color="color-28"
                      weight="regular"
                      className="text-center"
                    >
                      This email address will be used to communicate important
                      information to you about your appointments
                    </Typography>
                  </div>
                }

                {choices?.length ? (
                  <>
                    {choices.map((choice: Choice) => {
                      const key = choice?.place_holder
                        ?.toLowerCase()
                        ?.replace(/\s+/g, '_');
                      const name = getKeyName(key);
                      
                      if (
                        choice.unique_key ===
                        'emailAddressSelfMatchingClientChoiceV1'
                      ) {
                        return (
                          <EmailAutocompleteDomainSuggestion
                            key={name}
                            className={`${
                              errors[name] ? 'prefilled' : ''
                            }`}
                            placeholder={choice.place_holder}
                            type={
                              name === 'phone_no'
                                ? 'tel'
                                : choice?.metadata?.validation_type
                            }
                            data_validation_char_length={
                              choice?.metadata?.validation_char_length
                            }
                            required={true}
                            isForForm={true}
                            name={name}
                            value={formData[name]}
                            onChange={handleOnChange}
                            feedbackMessage={errors[name]}
                            isInvalid={
                              (validated || touched?.[name]) && errors[name]
                            }
                            isSelfOnboarding={true}
                          />
                        );
                      } else {
                        return (
                          <Input
                            key={name}
                            className={`signup-input ${
                              errors[name] ? 'prefilled' : ''
                            }`}
                            placeholder={choice.place_holder}
                            type={
                              name === 'phone_no'
                                ? 'tel'
                                : choice?.metadata?.validation_type
                            }
                            data_validation_char_length={
                              choice?.metadata?.validation_char_length
                            }
                            required={true}
                            isForForm={true}
                            name={name}
                            value={formData[name]}
                            onChange={onChange}
                            feedbackMessage={errors[name]}
                            isInvalid={
                              (validated || touched?.[name]) && errors[name]
                            }
                            isSelfOnboarding={true}
                          />
                        );
                      }
                    })}
                  </>
                ) : (
                  <EmailAutocompleteDomainSuggestion
                    className={`signup-input ${
                      isEmpty(inValidMessage('email')) ? 'prefilled' : ''
                    }`}
                    placeholder="email address"
                    type="email"
                    required={true}
                    isForForm={true}
                    name={'email'}
                    value={formData.email}
                    onChange={onChange}
                    feedbackMessage={errors.email}
                    isInvalid={(validated || touched?.email) && errors?.email}
                    isSelfOnboarding={true}
                  />
                )}

                <Button
                  label="Send Verification Link"
                  disabled={isDisabled}
                  type="submit"
                  className="d-block reset-btn"
                  textSize="size-14"
                  textWeight="bold"
                  isProcessing={loading}
                />

                <div className="text-center forgot-link-container">
                  <Typography
                    variant="size-14"
                    color="color-28"
                    className="d-inline-block"
                  >
                    Already have an account?&nbsp;{' '}
                  </Typography>
                  <Link to="/">
                    <Typography
                      variant="size-18"
                      weight="bold"
                      color="color-2"
                      className="login-btn"
                    >
                      Login
                    </Typography>
                  </Link>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    );
};

export default SelfOnboarding;
